import React, { useState, useEffect } from "react";
import TopBar from "../TopBar";
import { useNavigate } from "react-router-dom";
import Loading from "../Loading";
import axiosInstance from "../../axiosInstance";
import { toast } from "../../utils/toast";
import { 
    FaClock, 
    FaUsers, 
    FaCheckCircle, 
    FaCalendarCheck, 
    FaHome,
    FaHospital,
    FaExclamationTriangle,
    FaRedo
} from "react-icons/fa";
import BottomNav from "../BottomNav";

export default function QueueWait() {
    const user = JSON.parse(localStorage.getItem("userInfo"));
    const navigate = useNavigate();
    const [checked, setChecked] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(new Date());

    const getCheckedIn = async () => {
        try {
            setError(false);
            const res = await axiosInstance.get(`/visits/checked-in?patient=${user.id}`);
            console.log('checkedin', res.data);
            setChecked(res.data.data);
            setLastUpdated(new Date());
        } catch (err) {
            console.error('Error fetching check-in status:', err);
            setError(true);
            setChecked(null);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setLoading(true);
        getCheckedIn();
    };

    useEffect(() => {
        getCheckedIn();

        const intervalId = setInterval(() => {
            getCheckedIn();
        }, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, []);

    const formatTime = (date) => {
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };

    const getQueueNumber = () => {
        return checked?.checked_in?.queuenum ?? checked?.checked_in?.queue_num ?? '--';
    };

    const getWaitingCount = () => {
        return checked?.waiting ?? 0;
    };

    const getWaitTime = () => {
        const waitTime = checked?.wait_time ?? 0;
        return Math.round(waitTime);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <TopBar title={"Queue Status"} url={'/homedashboard'} />
                <Loading variant="medical" fullScreen={true} message="Checking your queue status..." />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <TopBar title={"Queue Status"} url={'/homedashboard'} />
            
            <div className="px-4 py-6 pb-24">
                {checked && !error ? (
                    <>
                        {/* Queue Number Card */}
                        <div className="bg-primary100 rounded-3xl p-8 mb-6 shadow-xl text-white">
                            <div className="text-center">
                                <div className="flex items-center justify-center mb-4">
                                    <div className="w-1 h-8 bg-white/30 rounded-full mr-4"></div>
                                    <h1 className="text-2xl font-bold">Your Queue Number</h1>
                                    <div className="w-1 h-8 bg-white/30 rounded-full ml-4"></div>
                                </div>
                                
                                <div className="relative">
                                    <div className="w-32 h-32 mx-auto bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/30 mb-4">
                                        <span className="text-6xl font-bold text-white">
                                            {getQueueNumber()}
                                        </span>
                                    </div>
                                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                                        <FaCheckCircle className="text-white text-lg" />
                                    </div>
                                </div>
                                
                                <p className="text-white/90 text-lg font-medium">
                                    You're checked in and waiting
                                </p>
                            </div>
                        </div>

                        {/* Status Cards */}
                        <div className="grid grid-cols-2 gap-4 mb-6">
                            {/* Waiting Count */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="p-3 bg-blue-100 rounded-xl">
                                        <FaUsers className="text-blue-600 text-xl" />
                                    </div>
                                    <span className="text-2xl font-bold text-gray-800">
                                        {getWaitingCount()}
                                    </span>
                                </div>
                                <p className="text-gray-600 font-medium">People Ahead</p>
                                <p className="text-gray-400 text-sm">In the queue</p>
                            </div>

                            {/* Wait Time */}
                            <div className="bg-white rounded-2xl p-6 shadow-lg">
                                <div className="flex items-center justify-between mb-3">
                                    <div className="p-3 bg-orange-100 rounded-xl">
                                        <FaClock className="text-orange-600 text-xl" />
                                    </div>
                                    <span className="text-2xl font-bold text-gray-800">
                                        {getWaitTime()}
                                    </span>
                                </div>
                                <p className="text-gray-600 font-medium">Minutes</p>
                                <p className="text-gray-400 text-sm">Waiting time</p>
                            </div>
                        </div>

                        {/* Status Information */}
                        <div className="bg-white rounded-2xl p-6 shadow-lg mb-6">
                            <div className="flex items-center mb-4">
                                <div className="p-2 bg-green-100 rounded-lg mr-3">
                                    <FaCalendarCheck className="text-green-600" />
                                </div>
                                <h3 className="text-lg font-bold text-gray-800">Check-in Status</h3>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-600">Status</span>
                                    <div className="flex items-center">
                                        <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                                        <span className="text-green-600 font-semibold">Checked In</span>
                                    </div>
                                </div>
                                
                                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                                    <span className="text-gray-600">Last Updated</span>
                                    <span className="text-gray-800 font-medium">
                                        {formatTime(lastUpdated)}
                                    </span>
                                </div>
                                
                                <div className="flex items-center justify-between py-2">
                                    <span className="text-gray-600">Auto Refresh</span>
                                    <div className="flex items-center">
                                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></div>
                                        <span className="text-blue-600 font-medium">Every 10s</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="space-y-3 pb-8">
                            <button 
                                onClick={handleRefresh}
                                className="w-full bg-primary100 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-2"
                            >
                                <FaRedo className="text-xl" />
                                <span>Refresh Status</span>
                            </button>
                            
                            <button 
                                onClick={() => navigate('/homedashboard')} 
                                className="w-full bg-white text-primary100 py-4 rounded-2xl font-bold text-lg shadow-lg border-2 border-primary100 hover:bg-primary100 hover:text-white transition-all duration-300 flex items-center justify-center space-x-2"
                            >
                                <FaHome className="text-xl" />
                                <span>Back to Home</span>
                            </button>
                        </div>
                    </>
                ) : (
                    /* Empty State - Not Checked In */
                    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4 pb-16">
                        <div className="bg-white rounded-3xl p-8 shadow-xl text-center max-w-md w-full">
                            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                                <FaExclamationTriangle className="text-gray-400 text-3xl" />
                            </div>
                            
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">
                                Not Checked In
                            </h2>
                            
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                You haven't checked in today or your session has ended. 
                                Please check in at the hospital reception to join the queue.
                            </p>
                            
                            <div className="bg-blue-50 rounded-2xl p-4 mb-6">
                                <div className="flex items-center justify-center mb-2">
                                    <FaHospital className="text-blue-600 mr-2" />
                                    <span className="text-blue-800 font-semibold">How to Check In</span>
                                </div>
                                <p className="text-blue-700 text-sm">
                                    Visit the hospital reception desk with your appointment details 
                                    or scan the QR code at the check-in desk.
                                </p>
                            </div>
                            
                            <div className="space-y-3">
                                <button 
                                    onClick={handleRefresh}
                                    className="w-full bg-primary100 text-white py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 flex items-center justify-center space-x-2"
                                >
                                    <FaRedo className="text-xl" />
                                    <span>Check Again</span>
                                </button>
                                
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <BottomNav color2={'text-primary100'} />
        </div>
    );
}
